import Axios from 'axios';
import { empresasLead } from './bff/bffClient';
import { mapGenericoLead, PersonType } from './bff/bffMappers';
import { getPathName, getPais, Pais, getApiHash, getApiToken } from './helper';

type Contacto = {
    origen?: string;
    rubro?: string;
    isMobile?: boolean;
    tipoCotizador?: string;
    landingUrl?: string;
    refererUrl?: string;
    nombre?: string;
    razonSocial?: string;
    apellido?: string;
    FechaNacimiento?: { dia: string; mes: string; año: string },
    codigoArea?: string;
    tipoDoc?: {
        value?: string;
        label?: string;
    };
    cuit?: string;
    numeroDoc?: string;
    telefono?: {
        prefijo?: string;
        numero?: string;
    };
    email?: string;
    token?: string;
    captchaToken: string;
    parametrosIniciales?: {
        origen?: string,
    };
    provincia?: {
        value?: string;
        label?: string;
    };
    localidad?: {
        value?: string;
        label?: string;
    };
    localidadLanding?: string;
    codigoPostal?: string;
    tipoVivienda?: string;
    /**
     * personType por defecto para rubros AMBOS, decidido por el form.
     * FormularioIndividuo (persona física) lo setea en 'PHYSICAL' para que un
     * rubro AMBOS (Embarcacion, AP...) lleve un personType válido para crmbridge.
     * NO pisa el tipo del rubro: un rubro JURIDICA (Incendio) sigue resolviendo
     * LEGAL y uno FISICA (Hogar) PHYSICAL. Solo aplica al body del BFF (AR).
     */
    defaultPersonTypeForBoth?: PersonType;
};

/**
 * tipoPersona por rubro de landing (rubros.js convención: FISICA|JURIDICA|AMBOS).
 * Para los rubros "ambos" §12.2 (personType obligatorio) el personType final lo
 * resuelve `mapGenericoLead` combinando esto con el tipoDoc del form.
 * Lookup case-insensitive. Lo no listado queda undefined (single-type: el BFF
 * resuelve sin personType, salvo que el tipoDoc lo determine).
 */
const TIPO_PERSONA_POR_RUBRO: Record<string, 'FISICA' | 'JURIDICA' | 'AMBOS'> = {
    // AMBOS (crmbridge EXIGE personType PHYSICAL|LEGAL; omitir = 400).
    AP: 'AMBOS',
    EMBARCACION: 'AMBOS',
    CAUCION: 'AMBOS',
    PREPAGA: 'AMBOS',
    'PLAN MEDICO': 'AMBOS',
    ASISTENCIA: 'AMBOS',
    // JURIDICA (crmbridge rechaza PHYSICAL; acepta LEGAL u omitido).
    ART: 'JURIDICA',
    COMERCIO: 'JURIDICA',
    INTEGRALES: 'JURIDICA',
    CONSORCIO: 'JURIDICA',
    TRANSPORTE: 'JURIDICA',
    AGRO: 'JURIDICA',
    INCENDIO: 'JURIDICA',
    CREDITOS: 'JURIDICA',
    RC: 'JURIDICA',
    TRO: 'JURIDICA',
    VIDA: 'JURIDICA',
    // FISICA (crmbridge rechaza LEGAL; acepta PHYSICAL u omitido).
    HOGAR: 'FISICA',
    CELULAR: 'FISICA',
    'MALA PRAXIS': 'FISICA',
};

/**
 * Lead de landings genéricos. El rubro es variable (lo trae `params.rubro`) y se
 * manda TAL CUAL. ROUTING POR PAÍS (el BFF es Argentina-only):
 * - AR: body tipado §12.2 (`mapGenericoLead`) al BFF. personType derivado del
 *   tipoDoc y/o del tipoPersona del rubro (AMBOS sin doc -> omitido).
 * - UY: flujo LEGACY al monolito (POST {monolito}/SetCotizacionLanding).
 * El país lo determina `pais` (lo pasa el form desde config.pais) o `getPais()`
 * (dominio). NO depende del string `origen` (los ~10 landings que no pasan origen
 * caen en default "SEGURARSE" pero igual rutean por el país real -> sin fuga UY al BFF).
 * Devuelve el envelope { data: { api_status, api_data } } que consume gracias.
 */
const guardarContactoGenerico = async (params: Contacto, origen: string = "SEGURARSE", extras?:Record<string, string>, pais: Pais = getPais()) => {

    const origenEfectivo = origen ? origen : params.parametrosIniciales?.origen ?? "";
    const rubro = params.rubro ?? "LANDING";

    if (pais === 'UY') {
        const data: Record<string, any> = {
            ...extras,
            origen: origenEfectivo || null,
            landingUrl: params.landingUrl ?? null,
            refererUrl: params.refererUrl ?? null,
            rubro: params.rubro ?? null,
            nombre: params.nombre ?? null,
            apellido: params.apellido ?? null,
            razonSocial: params.razonSocial ?? null,
            FechaNacimiento: params.FechaNacimiento
                ? `${params.FechaNacimiento.dia}/${params.FechaNacimiento.mes}/${params.FechaNacimiento.año}`
                : null,
            codigoArea: params.telefono?.prefijo ?? null,
            telefono: {
                codigoArea: params.telefono?.prefijo ?? null,
                numero: params.telefono?.numero ?? null,
            },
            email: params.email ?? null,
            cuit: params.cuit ?? null,
            numeroDoc: params.numeroDoc ?? null,
            tipoDoc: params.tipoDoc?.value ?? null,
            provincia: params.provincia?.value ?? "sin especificar",
            localidad: params.localidad?.value ?? params.localidadLanding,
            codigoPostal: params.codigoPostal ?? null,
            tipoCotizador: params.tipoCotizador ?? null,
            tipoVivienda: params.tipoVivienda ?? null,
            g_recaptcha_response: params.captchaToken ?? null,
            hash: await getApiHash() ?? null,
        };
        const headers = {
            'content-type': 'application/json',
            "Authorization": 'Bearer ' + await getApiToken(),
        };
        return await Axios.post(await getPathName() + "/SetCotizacionLanding", data, { headers })
            .then((response) => response)
            .catch((error) => error);
    }

    const body = mapGenericoLead({
        rubro,
        origen: origenEfectivo,
        tipoCotizador: params.tipoCotizador,
        landingUrl: params.landingUrl ?? (typeof window !== 'undefined' ? window.location.href : undefined),
        refererUrl: params.refererUrl,
        nombre: params.nombre,
        apellido: params.apellido,
        razonSocial: params.razonSocial,
        codigoArea: params.telefono?.prefijo,
        telefono: params.telefono?.numero,
        email: params.email,
        tipoDoc: params.tipoDoc?.value,
        cuit: params.cuit,
        numeroDoc: params.numeroDoc,
        provincia: params.provincia?.value,
        localidad: params.localidad?.value ?? params.localidadLanding,
        codigoPostal: params.codigoPostal,
        fechaNacimiento: params.FechaNacimiento
            ? `${params.FechaNacimiento.dia}/${params.FechaNacimiento.mes}/${params.FechaNacimiento.año}`
            : undefined,
        tipoVivienda: params.tipoVivienda,
        tipoPersonaRubro: TIPO_PERSONA_POR_RUBRO[(rubro || "").toUpperCase()],
        defaultPersonTypeForBoth: params.defaultPersonTypeForBoth,
        extras,
    });

    try {
        const apiData = await empresasLead(body);
        return { data: { api_status: 200, api_data: apiData } };
    } catch (error) {
        return error;
    }
};

export default guardarContactoGenerico;
